import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Table, Button, Tag, Space, Select, DatePicker, Modal, Alert, Empty, Spin } from 'antd';
import { PlayCircleOutlined, ClockCircleOutlined, CalendarOutlined, InfoCircleOutlined, UserOutlined, EnvironmentOutlined, WarningOutlined } from '@ant-design/icons';
import { API_BASE, authHeaders } from '../config/api';
import dayjs from 'dayjs';
import './admin.css';

const { Option } = Select;
const { RangePicker } = DatePicker;

// Load rrweb-player CSS and JS from CDN
const loadRRWebPlayer = () => {
  return new Promise((resolve, reject) => {
    if (window.rrwebPlayer) return resolve();
    
    // Load CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/rrweb-player@latest/dist/style.css';
    document.head.appendChild(link);
    
    // Load JS
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/rrweb-player@latest/dist/index.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load rrweb-player'));
    document.head.appendChild(script);
  });
};

const SessionRecordings = () => {
  const [loading, setLoading] = useState(true);
  const [recordings, setRecordings] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [websites, setWebsites] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [specificDate, setSpecificDate] = useState(null);
  const [replayModalVisible, setReplayModalVisible] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [showGuide, setShowGuide] = useState(true);
  const [rrwebPlayerReady, setRRWebPlayerReady] = useState(false);
  const rrwebPlayerRef = useRef(null);
  const playerContainerRef = useRef(null);

  const loadWebsites = async () => {
    try {
      console.log('[SessionRecordings] Loading websites...');
      const res = await fetch(`${API_BASE}/my/websites`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      const data = await res.json();
      console.log('[SessionRecordings] Websites response:', data);
      
      // Handle both response formats: {websites: [...]} or {items: [...]}
      const websitesList = data.websites || data.items || [];
      
      if (res.ok && websitesList.length > 0) {
        setWebsites(websitesList);
        setSelectedWebsite(websitesList[0].id);
        console.log('[SessionRecordings] Selected website:', websitesList[0].id);
      } else {
        console.log('[SessionRecordings] No websites found');
        setLoading(false);
      }
    } catch (err) {
      console.error('[SessionRecordings] Error loading websites:', err);
      setLoading(false);
    }
  };

  const loadRecordings = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/analytics/session-recordings?website_id=${selectedWebsite}`;
      
      if (specificDate) {
        url += `&date=${specificDate.format('YYYY-MM-DD')}`;
      } else if (dateRange && dateRange[0] && dateRange[1]) {
        url += `&start_date=${dateRange[0].format('YYYY-MM-DD')}&end_date=${dateRange[1].format('YYYY-MM-DD')}`;
      }
      
      console.log('[SessionRecordings] Fetching:', url);
      const res = await fetch(url, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      const data = await res.json();
      console.log('[SessionRecordings] Response status:', res.status);
      console.log('[SessionRecordings] Response data:', data);
      
      if (res.ok) {
        setRecordings(data.recordings || []);
        console.log('[SessionRecordings] Set recordings count:', data.recordings?.length || 0);
      } else {
        console.error('[SessionRecordings] Error response:', data);
      }
    } catch (err) {
      console.error('[SessionRecordings] Error loading recordings:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedWebsite, specificDate, dateRange]);

  useEffect(() => {
    loadWebsites();
    loadRRWebPlayer()
      .then(() => setRRWebPlayerReady(true))
      .catch(err => console.error('Failed to load rrweb-player:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedWebsite) {
      loadRecordings();
    }
  }, [selectedWebsite, loadRecordings]);

  const handlePlayRecording = (recording) => {
    setSelectedRecording(recording);
    setReplayModalVisible(true);
  };

  const handleCloseReplay = () => {
    setReplayModalVisible(false);
    if (rrwebPlayerRef.current) {
      try {
        rrwebPlayerRef.current.pause();
        rrwebPlayerRef.current = null;
      } catch (e) {}
    }
  };

  const getEventColor = (type) => {
    const map = { click: '#3b82f6', scroll: '#10b981', pageview: '#8b5cf6', navigation: '#f59e0b', form_interaction: '#ef4444' };
    return map[type] || '#94a3b8';
  };

  const getEventIcon = (type) => {
    const map = { click: '🖱️', scroll: '↕️', pageview: '📄', navigation: '🔗', form_interaction: '📝' };
    return map[type] || '•';
  };

  // Initialize rrweb player when recording is selected
  useEffect(() => {
    if (!selectedRecording || !replayModalVisible || !rrwebPlayerReady || !playerContainerRef.current) return;
    
    const rrwebData = selectedRecording.rrwebData;
    if (!rrwebData || !Array.isArray(rrwebData) || rrwebData.length === 0) return;
    
    // Cleanup previous player
    if (rrwebPlayerRef.current) {
      try {
        rrwebPlayerRef.current.pause();
      } catch (e) {}
    }
    
    // Clear container
    playerContainerRef.current.innerHTML = '';
    
    // Create new player
    try {
      if (window.rrwebPlayer) {
        rrwebPlayerRef.current = new window.rrwebPlayer({
          target: playerContainerRef.current,
          props: {
            events: rrwebData,
            width: 700,
            height: 450,
            autoPlay: false,
            showController: true,
            speedOption: [0.5, 1, 2, 4, 8],
            tags: {},
            mouseTail: {
              duration: 500,
              lineCap: 'round',
              lineWidth: 3,
              strokeStyle: '#6366f1'
            }
          }
        });
      }
    } catch (err) {
      console.error('Error initializing rrweb player:', err);
    }
  }, [selectedRecording, replayModalVisible, rrwebPlayerReady]);

  const columns = [
    {
      title: 'Session ID',
      dataIndex: 'session_id',
      key: 'session_id',
      width: 150,
      render: (text) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{text}</span>
      )
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      render: (duration) => (
        <Tag color="blue" icon={<ClockCircleOutlined />}>
          {Math.floor(duration / 60)}m {duration % 60}s
        </Tag>
      )
    },
    {
      title: 'Pages Visited',
      dataIndex: 'pages_visited',
      key: 'pages_visited',
      width: 100,
      align: 'center',
      render: (count) => (
        <Tag color="purple">{count} pages</Tag>
      )
    },
    {
      title: 'Events',
      dataIndex: 'event_count',
      key: 'event_count',
      width: 100,
      align: 'center',
      render: (count) => (
        <Tag color="green">{count} events</Tag>
      )
    },
    {
      title: 'Date & Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (timestamp) => (
        <div>
          <div>{dayjs(timestamp).format('MMM DD, YYYY')}</div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>
            {dayjs(timestamp).format('hh:mm A')}
          </div>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const statusConfig = {
          completed: { color: 'success', text: 'Completed' },
          abandoned: { color: 'warning', text: 'Abandoned' },
          active: { color: 'processing', text: 'Active' }
        };
        const config = statusConfig[status] || statusConfig.completed;
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          onClick={() => handlePlayRecording(record)}
          size="small"
        >
          Replay
        </Button>
      )
    }
  ];

  if (loading) {
    return (
      <div className="abo-content">
        <h2>Session Recordings</h2>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: '#94a3b8' }}>Loading session recordings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="abo-content">
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0 }}>Session Recordings</h2>
            <p style={{ color: '#94a3b8', marginTop: 4 }}>
              Watch real user sessions to understand Behaviour and identify issues
            </p>
          </div>
          <Select
            value={selectedWebsite}
            onChange={setSelectedWebsite}
            style={{ width: 250 }}
            placeholder="Select website"
          >
            {websites.map(site => (
              <Option key={site.id} value={site.id}>{site.url}</Option>
            ))}
          </Select>
        </div>

        {/* Date Filters */}
        <Card size="small" style={{ background: 'rgba(99, 102, 241, 0.05)', border: '1px solid rgba(99, 102, 241, 0.2)', marginBottom: 16 }}>
          <Space size="middle" wrap>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CalendarOutlined style={{ color: '#6366f1' }} />
              <span style={{ fontWeight: 500 }}>Filter by Date:</span>
            </div>
            
            <DatePicker
              placeholder="Specific Date (e.g., Dec 31, 2025)"
              value={specificDate}
              onChange={(date) => {
                setSpecificDate(date);
                if (date) setDateRange(null);
              }}
              format="MMM DD, YYYY"
              style={{ width: 220 }}
              allowClear
            />
            
            <span style={{ color: '#94a3b8' }}>or</span>
            
            <RangePicker
              placeholder={['Start Date', 'End Date']}
              value={dateRange}
              onChange={(dates) => {
                setDateRange(dates);
                if (dates) setSpecificDate(null);
              }}
              format="MMM DD, YYYY"
              style={{ width: 280 }}
              allowClear
            />
            
            {(specificDate || dateRange) && (
              <Alert
                message={
                  specificDate 
                    ? `Showing recordings for ${specificDate.format('MMM DD, YYYY')}`
                    : `Showing recordings from ${dateRange[0].format('MMM DD, YYYY')} to ${dateRange[1].format('MMM DD, YYYY')}`
                }
                type="info"
                showIcon
                closable={false}
                style={{ marginLeft: 8 }}
              />
            )}
          </Space>
        </Card>

        {/* Guide Section */}
        {showGuide && (
          <Alert
            message={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <InfoCircleOutlined />
                <strong>How to Use Session Recordings</strong>
              </div>
            }
            description={
              <div>
                <p style={{ marginBottom: 8 }}>
                  <strong>What are Session Recordings?</strong> Watch exactly how users interact with your website - every click, scroll, and page navigation.
                </p>
                <p style={{ marginBottom: 8 }}>
                  <strong>How to replay:</strong> Click the "Replay" button on any session to watch the recording. Use playback controls to pause, speed up, or skip through the session.
                </p>
                <p style={{ marginBottom: 8 }}>
                  <strong>What to look for:</strong>
                </p>
                <ul style={{ marginBottom: 8, paddingLeft: 20 }}>
                  <li>Confusion points where users hesitate or backtrack</li>
                  <li>Form abandonment - see exactly where users give up</li>
                  <li>Navigation issues - paths users take that don't make sense</li>
                  <li>Error encounters and how users react</li>
                  <li>Mobile vs desktop Behaviour differences</li>
                </ul>
                <p style={{ marginBottom: 0 }}>
                  <strong>Privacy:</strong> Sensitive data like passwords and credit cards are automatically masked in recordings.
                </p>
              </div>
            }
            type="info"
            showIcon
            closable
            onClose={() => setShowGuide(false)}
            style={{ marginBottom: 24 }}
          />
        )}
      </div>

      {recordings.length === 0 ? (
        <Card>
          <Empty
            image={<PlayCircleOutlined style={{ fontSize: 64, color: '#94a3b8' }} />}
            description={
              <div>
                <p style={{ fontSize: 16, fontWeight: 500 }}>No session recordings available</p>
                <p style={{ fontSize: 12, color: '#94a3b8' }}>
                  Session recordings will appear here once users visit your website with the tracking script installed.
                </p>
              </div>
            }
          />
        </Card>
      ) : (
        <Card>
          <Table
            columns={columns}
            dataSource={recordings}
            rowKey="session_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} recordings`
            }}
            scroll={{ x: 1200 }}
          />
        </Card>
      )}

      {/* Replay Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PlayCircleOutlined style={{ color: '#6366f1' }} />
            <span>Session Replay</span>
            {selectedRecording && (
              <Tag color="blue" style={{ marginLeft: 8, fontFamily: 'monospace', fontSize: 11 }}>
                {selectedRecording.session_id?.slice(0, 20)}…
              </Tag>
            )}
          </div>
        }
        open={replayModalVisible}
        onCancel={handleCloseReplay}
        width={1100}
        footer={null}
        centered
        destroyOnClose
      >
        {selectedRecording && (() => {
          const events = selectedRecording.events || [];
          const totalEvents = events.length;

          return (
            <div>
              {/* ── Session Meta Bar ── */}
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', padding: '10px 14px', background: 'rgba(99,102,241,0.06)', borderRadius: 10, marginBottom: 14, border: '1px solid rgba(99,102,241,0.15)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <UserOutlined style={{ color: '#6366f1' }} />
                  <span style={{ fontSize: 13 }}>{selectedRecording.user_info?.name || 'Anonymous'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <EnvironmentOutlined style={{ color: '#10b981' }} />
                  <span style={{ fontSize: 13, color: '#64748b' }}>{selectedRecording.user_info?.location || '—'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 13 }}>
                  <ClockCircleOutlined />
                  {Math.floor(selectedRecording.duration / 60)}m {selectedRecording.duration % 60}s
                </div>
                <div style={{ fontSize: 13, color: '#64748b' }}>📄 {selectedRecording.pages_visited} pages</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>⚡ {selectedRecording.event_count} events</div>
                <div style={{ fontSize: 13, color: '#64748b', marginLeft: 'auto' }}>
                  {dayjs(selectedRecording.timestamp).format('MMM DD, YYYY · hh:mm A')}
                </div>
              </div>

              {/* ── Main player area ── */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 14 }}>

                {/* Left: RRWeb Player or Fallback */}
                <div style={{ background: '#f8fafc', borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                  {selectedRecording.rrwebData && Array.isArray(selectedRecording.rrwebData) && selectedRecording.rrwebData.length > 0 ? (
                    // Real rrweb player
                    <div 
                      ref={playerContainerRef} 
                      style={{ 
                        minHeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#fff'
                      }}
                    />
                  ) : (
                    // Fallback when no rrweb data
                    <div style={{ padding: 40, textAlign: 'center', minHeight: 500, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <WarningOutlined style={{ fontSize: 48, color: '#f59e0b', marginBottom: 16 }} />
                      <div style={{ fontSize: 16, fontWeight: 500, color: '#0f172a', marginBottom: 8 }}>
                        No Recording Data Available
                      </div>
                      <div style={{ fontSize: 13, color: '#64748b', maxWidth: 400, lineHeight: 1.6 }}>
                        This session was recorded before DOM snapshot tracking was enabled. 
                        To see full page replays, add the enhanced tracker snippet to your website.
                      </div>
                      <div style={{ marginTop: 24, padding: 16, background: '#fef3c7', borderRadius: 8, border: '1px solid #fde68a', maxWidth: 500 }}>
                        <div style={{ fontSize: 12, color: '#92400e', marginBottom: 8, fontWeight: 500 }}>
                          📋 Use Enhanced Tracker
                        </div>
                        <div style={{ fontSize: 11, color: '#78350f', lineHeight: 1.5 }}>
                          Replace your current tracker with <code style={{ background: '#fff', padding: '2px 4px', borderRadius: 3 }}>tracker-rrweb.js</code> to enable full session recording with real page content.
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right: Event timeline */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', fontSize: 12, color: '#64748b', fontWeight: 500 }}>Event Timeline</div>
                  <div style={{ flex: 1, overflowY: 'auto', maxHeight: 440 }}>
                    {totalEvents === 0 ? (
                      <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>No events recorded</div>
                    ) : (
                      events.map((ev, idx) => {
                        const evType = ev.type || ev.event_type || 'unknown';
                        const evDesc = ev.description || ev.event_description || evType;
                        return (
                          <div
                            key={idx}
                            style={{
                              padding: '8px 12px',
                              borderBottom: '1px solid #f1f5f9',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 8,
                            }}
                          >
                            <span style={{ fontSize: 14, lineHeight: 1.3, flexShrink: 0 }}>{getEventIcon(evType)}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: getEventColor(evType), color: '#fff', fontWeight: 500 }}>{evType}</span>
                                <span style={{ fontSize: 10, color: '#94a3b8' }}>{ev.timestamp || ev.timestamp_offset || ''}</span>
                              </div>
                              <div style={{ fontSize: 11, color: '#475569', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {evDesc}
                              </div>
                              {ev.page_url && (
                                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.page_url}</div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default SessionRecordings;
